// Plays lesson and check-in prompts through one shared Web Audio context.
//
// Why Web Audio and not <audio> / speechSynthesis: on iOS Safari, playback through
// a media element or speechSynthesis after a recognition session leaves the next
// SpeechRecognition "open but deaf" (WebKit bug 321436). Web Audio playback does
// not; verified on a real iPhone (issue #240).
//
// iOS only lets an AudioContext start from a user gesture, so playPrompt() must be
// called from a tap handler: it creates/resumes the context synchronously, before
// any await. At most one prompt plays at a time.
//
// Screen lock and app switch: iOS can deactivate audio output while the page is
// hidden yet leave the context reporting 'running' with no statechange, so later
// clips "play" silently (issue #243). When the page becomes hidden we stop the
// current prompt and close the context; the next playPrompt() forces an audio
// session category change and creates a fresh context inside the tap. Decoded
// buffers are not tied to a context and are reused.
//
// Clips are fetched, so a clip URL must be same-origin, a data: URL (what the
// audio API returns today), or served with CORS headers.

import { preparePlayback, reactivatePlayback } from './session';
import { diag } from './diag';

export interface PromptCallbacks {
  /** The clip played to its end. */
  onEnded?: () => void;
  /** iOS interrupted the context mid-prompt (call, background, lock); the clip was stopped. */
  onInterrupted?: () => void;
  /** Another prompt started, so this one was stopped (or never started). */
  onSuperseded?: () => void;
}

export interface PromptPlayback {
  /** Stop now (or cancel before it starts). Never calls onEnded. Safe to call repeatedly. */
  stop(): void;
  /** Resolves once the clip starts (or was cancelled first); rejects if it can't play. */
  readonly started: Promise<void>;
}

interface ActivePrompt {
  stop(): void;
  interrupt(): void;
  /** The page was hidden: stop even a prompt that hasn't started yet. */
  hide(): void;
  supersede(): void;
}

type AudioContextCtor = new () => AudioContext;

let ctx: AudioContext | null = null;
let hiddenSinceLastPlay = false;
let active: ActivePrompt | null = null;
let visibilityListening = false;
const buffers = new Map<string, Promise<AudioBuffer>>();

function onVisibilityChange(): void {
  diag('promptPlayer: visibility →', document.visibilityState);
  if (document.visibilityState !== 'hidden') return;
  hiddenSinceLastPlay = true;
  active?.hide();
  if (ctx) {
    // Closed now, not in the next tap: on iOS, a context closing right as the
    // fresh one starts can leave output off.
    closeStaleContext(ctx);
    ctx = null;
    diag('promptPlayer: closed AudioContext on hide');
  }
}

function closeStaleContext(stale: AudioContext): void {
  try {
    stale.close().catch((err: unknown) => {
      diag('promptPlayer: closing stale AudioContext failed', err);
    });
  } catch (err) {
    diag('promptPlayer: closing stale AudioContext failed', err);
  }
}

function getContext(): AudioContext {
  if (!visibilityListening) {
    document.addEventListener('visibilitychange', onVisibilityChange);
    visibilityListening = true;
  }
  if (ctx) return ctx;
  // Unprefixed only: the prefixed webkitAudioContext (Safari < 14.1) has a
  // callback-only decodeAudioData, and iOS speech recognition needs 14.5+ anyway.
  const Ctor = (window as unknown as { AudioContext?: AudioContextCtor })
    .AudioContext;
  if (!Ctor) throw new Error('Web Audio is not supported in this browser');
  const created = new Ctor();
  created.addEventListener('statechange', () => {
    diag('promptPlayer: AudioContext state →', created.state);
    // A replaced (stale) context closing must not touch the current prompt.
    if (created !== ctx) return;
    if (created.state !== 'running') active?.interrupt();
  });
  diag('promptPlayer: AudioContext created state=', created.state);
  ctx = created;
  return created;
}

function decode(context: AudioContext, url: string): Promise<AudioBuffer> {
  let pending = buffers.get(url);
  if (!pending) {
    pending = (async (): Promise<AudioBuffer> => {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(
          `Prompt audio fetch failed (status ${response.status})`
        );
      }
      return context.decodeAudioData(await response.arrayBuffer());
    })();
    buffers.set(url, pending);
    // Don't cache failures: the next tap retries.
    pending.catch(() => buffers.delete(url));
  }
  return pending;
}

/**
 * Play a prompt clip (any URL `fetch` accepts, including `data:` URLs). Must be
 * called from a user gesture. Stops whatever prompt is currently playing.
 */
export function playPrompt(
  url: string,
  callbacks: PromptCallbacks = {}
): PromptPlayback {
  active?.supersede();
  // Declare playback before the context is created or resumed (spike order).
  if (hiddenSinceLastPlay) {
    hiddenSinceLastPlay = false;
    reactivatePlayback('prompt');
  } else {
    preparePlayback('prompt');
  }

  let finished = false;
  let source: AudioBufferSourceNode | null = null;

  const teardown = (): void => {
    finished = true;
    if (source) {
      source.onended = null;
      try {
        source.stop();
      } catch {
        // already stopped
      }
      source.disconnect();
      source = null;
    }
    // Only the active prompt is ever torn down (a superseded one is already finished).
    active = null;
  };

  const entry: ActivePrompt = {
    stop: (): void => {
      if (finished) return;
      diag('promptPlayer: stop');
      teardown();
    },
    // Only an unfinished prompt is ever `active`, so no finished check here.
    supersede: (): void => {
      diag('promptPlayer: superseded by another prompt');
      teardown();
      callbacks.onSuperseded?.();
    },
    interrupt: (): void => {
      // Only a clip that is actually playing can be interrupted; a pending start
      // sees the context state itself after resume().
      if (finished || !source) return;
      diag('promptPlayer: interrupted mid-prompt');
      teardown();
      callbacks.onInterrupted?.();
    },
    // A start still in flight would otherwise play on the stale context after unlock.
    hide: (): void => {
      diag('promptPlayer: page hidden mid-prompt');
      teardown();
      callbacks.onInterrupted?.();
    },
  };
  active = entry;

  let context: AudioContext;
  let resumed: Promise<void>;
  try {
    context = getContext();
    // Synchronous call inside the gesture — iOS requires it.
    resumed =
      context.state === 'running' ? Promise.resolve() : context.resume();
  } catch (err) {
    teardown();
    return { stop: entry.stop, started: Promise.reject(err) };
  }

  const started = (async (): Promise<void> => {
    try {
      await resumed;
      if (context.state !== 'running') {
        throw new Error(`AudioContext not running (state=${context.state})`);
      }
      const buffer = await decode(context, url);
      if (finished) return;
      const node = context.createBufferSource();
      node.buffer = buffer;
      node.connect(context.destination);
      // teardown() nulls this handler, so it only fires for a natural end.
      node.onended = (): void => {
        diag('promptPlayer: ended');
        teardown();
        callbacks.onEnded?.();
      };
      source = node;
      node.start();
      diag('promptPlayer: started');
    } catch (err) {
      // Cancelled or superseded while starting: nothing to report.
      if (finished) return;
      diag('promptPlayer: failed', err);
      teardown();
      throw err;
    }
  })();

  return { stop: entry.stop, started };
}

/**
 * True when a clip can start without a tap: the shared context was started by
 * an earlier tap and is still running. Never creates or resumes the context, so
 * a caller that checks this first can't leave a clip waiting for a gesture.
 */
export function canAutoPlay(): boolean {
  return ctx !== null && ctx.state === 'running' && !hiddenSinceLastPlay;
}

/**
 * Start (or resume) the shared context so later clips can begin by themselves
 * (see {@link canAutoPlay}). Call synchronously from a tap handler; a failure
 * only means those clips wait for their Play button.
 */
export function unlockPromptPlayback(): void {
  // Same prelude as playPrompt: declare playback first, and after the page was
  // hidden force the session change that reactivates iOS audio (issue #243).
  if (hiddenSinceLastPlay) {
    hiddenSinceLastPlay = false;
    reactivatePlayback('unlock');
  } else {
    preparePlayback('unlock');
  }
  try {
    const context = getContext();
    if (context.state !== 'running') {
      context.resume().catch((err: unknown) => {
        diag('promptPlayer: unlock failed', err);
      });
    }
  } catch (err) {
    diag('promptPlayer: unlock failed', err);
  }
}

/** Test-only: drop the shared context, cache and active prompt. */
export function resetPromptPlayerForTests(): void {
  document.removeEventListener('visibilitychange', onVisibilityChange);
  visibilityListening = false;
  ctx = null;
  hiddenSinceLastPlay = false;
  active = null;
  buffers.clear();
}
